import { budget, emergencyFund, savingsGoal } from "../../lib/calculators/planning";
import { CAD } from "../../lib/format";

export const budgetConfig = {
  slug: "budget-calculator-canada",
  name: "Budget Calculator",
  category: "budgeting",
  icon: "PiggyBank",
  tagline: "Build a monthly budget and compare it to the 50/30/20 rule.",
  applicableYear: 2026,
  lastUpdated: "2026-01-01",
  recommendedProduct: "personal-finance-dashboard",
  relatedTools: ["emergency-fund-calculator-canada", "savings-goal-calculator-canada"],
  seo: {
    title: "Budget Calculator Canada (2026) — 50/30/20 Monthly Budget",
    description: "Build a simple monthly budget and see how your spending compares to the 50/30/20 rule. Find out how much you have left to save.",
    keywords: ["budget calculator canada", "50 30 20 budget", "monthly budget calculator", "personal budget"],
  },
  inputs: [
    { id: "monthlyIncome", label: "Monthly after-tax income", type: "currency", default: 5000, min: 0 },
    { id: "needs", label: "Needs (rent, food, bills)", type: "currency", default: 2500, min: 0, help: "Essential expenses you can't avoid." },
    { id: "wants", label: "Wants (dining, entertainment)", type: "currency", default: 1200, min: 0, help: "Lifestyle and discretionary spending." },
    { id: "savings", label: "Savings & debt payments", type: "currency", default: 800, min: 0, help: "Money toward savings, investments and extra debt payoff." },
  ],
  compute: (v) => {
    const r = budget(v);
    return {
      primary: { label: r.leftover >= 0 ? "Unallocated / surplus" : "Over budget by", value: Math.abs(r.leftover), type: "currency" },
      secondary: [
        { label: `Needs (${r.needsPct}%)`, value: r.needs, type: "currency", hint: `Guideline: ${CAD(r.guideline.needs)}` },
        { label: `Wants (${r.wantsPct}%)`, value: r.wants, type: "currency", hint: `Guideline: ${CAD(r.guideline.wants)}` },
        { label: `Savings (${r.savingsPct}%)`, value: r.savings, type: "currency", hint: `Guideline: ${CAD(r.guideline.savings)}` },
        { label: "Total allocated", value: r.totalAllocated, type: "currency" },
      ],
      chart: {
        type: "pie",
        title: "Where your money goes",
        data: [
          { name: "Needs", value: Math.round(r.needs) },
          { name: "Wants", value: Math.round(r.wants) },
          { name: "Savings", value: Math.round(r.savings) },
          ...(r.leftover > 0 ? [{ name: "Unallocated", value: Math.round(r.leftover) }] : []),
        ],
      },
      status: r.leftover < 0
        ? { type: "warning", text: `You're spending ${CAD(Math.abs(r.leftover))} more than you earn. Trim wants or increase income.` }
        : { type: "success", text: `You have ${CAD(r.leftover)} unallocated — consider directing it to savings or debt.` },
    };
  },
  content: {
    howItWorks: "The 50/30/20 rule is a simple budgeting framework: aim to spend about 50% of after-tax income on needs, 30% on wants, and 20% on savings and debt repayment. This tool compares your actual spending to those targets and shows what's left over.",
    inputsExplained: [
      { term: "Needs", desc: "Essentials: rent/mortgage, groceries, utilities, insurance, minimum debt payments." },
      { term: "Wants", desc: "Non-essentials: dining out, subscriptions, travel, hobbies." },
      { term: "Savings", desc: "Emergency fund, investments, and extra debt payments." },
    ],
    example: "On $5,000/month, the 50/30/20 targets are $2,500 needs, $1,500 wants and $1,000 savings.",
    assumptions: ["Income is after-tax (take-home).", "50/30/20 is a guideline, not a rule.", "All values are monthly."],
    limitations: ["High cost-of-living areas may need more than 50% for needs.", "Doesn't track individual transactions.", "A starting framework — adjust to your goals."],
  },
  faq: [
    { q: "What is the 50/30/20 rule?", a: "A budgeting guideline: 50% of after-tax income to needs, 30% to wants, 20% to savings and debt repayment. Adjust the ratios to fit your life." },
    { q: "What if my needs are over 50%?", a: "Common in expensive cities. Focus on keeping wants in check and protecting at least some savings, and look for ways to increase income over time." },
  ],
};

export const emergencyFundConfig = {
  slug: "emergency-fund-calculator-canada",
  name: "Emergency Fund Calculator",
  category: "budgeting",
  icon: "PiggyBank",
  tagline: "Find your emergency fund target and time to reach it.",
  applicableYear: 2026,
  lastUpdated: "2026-01-01",
  recommendedProduct: "personal-finance-dashboard",
  relatedTools: ["budget-calculator-canada", "savings-goal-calculator-canada"],
  seo: {
    title: "Emergency Fund Calculator Canada (2026) — How Much Should You Save?",
    description: "Calculate your ideal emergency fund based on monthly expenses, and see how long it will take to reach your goal with regular saving.",
    keywords: ["emergency fund calculator", "how much emergency fund", "rainy day fund canada", "3 to 6 months expenses"],
  },
  inputs: [
    { id: "monthlyExpenses", label: "Essential monthly expenses", type: "currency", default: 3500, min: 0 },
    { id: "monthsTarget", label: "Months of coverage", type: "slider", default: 6, min: 1, max: 12, step: 1, suffix: " mo", help: "3 months is a common minimum; 6+ is safer." },
    { id: "currentSavings", label: "Current emergency savings", type: "currency", default: 5000, min: 0 },
    { id: "monthlyContribution", label: "Monthly savings toward fund", type: "currency", default: 500, min: 0 },
  ],
  compute: (v) => {
    const r = emergencyFund(v);
    return {
      primary: { label: `${v.monthsTarget}-month emergency fund target`, value: r.targetAmount, type: "currency" },
      secondary: [
        { label: "Currently saved", value: r.currentSavings, type: "currency" },
        { label: "Still needed", value: r.gap, type: "currency" },
        { label: "Funded", value: r.percentFunded, type: "percent" },
        { label: "Months already covered", value: r.monthsCovered, type: "number" },
        { label: "Time to reach goal", value: r.monthsToGoal ? `${r.monthsToGoal} months` : "Add a monthly amount", type: "raw" },
      ],
      chart: {
        type: "bar",
        title: "Progress to your target",
        data: [
          { x: "Saved", Amount: Math.round(r.currentSavings) },
          { x: "Still needed", Amount: Math.round(r.gap) },
        ],
        xKey: "x",
        series: [{ key: "Amount", name: "Amount" }],
      },
      status: r.percentFunded >= 100
        ? { type: "success", text: "You've reached your emergency fund target. Well done!" }
        : { type: "info", text: `You're ${r.percentFunded}% of the way there. Keep going!` },
    };
  },
  content: {
    howItWorks: "An emergency fund covers unexpected costs — job loss, car repairs, medical bills — without going into debt. The target is a multiple of your essential monthly expenses (commonly 3–6 months). This tool computes your target, your progress, and how long regular saving will take to close the gap.",
    inputsExplained: [
      { term: "Essential monthly expenses", desc: "The bare minimum to keep your household running: housing, food, utilities, insurance, transport." },
      { term: "Months of coverage", desc: "How many months of expenses you want banked. More is safer, especially with variable income." },
    ],
    example: "With $3,500/month in essentials and a 6-month goal, your target is $21,000. Saving $500/month closes a $16,000 gap in about 32 months.",
    assumptions: ["Uses essential (not total) monthly expenses.", "No investment growth on the fund (kept in cash/HISA).", "Contributions are consistent."],
    limitations: ["Doesn't account for interest earned in a high-interest savings account.", "Everyone's ideal size differs by job stability and dependents.", "Keep the fund liquid and separate from spending."],
  },
  faq: [
    { q: "How big should my emergency fund be?", a: "Commonly 3–6 months of essential expenses. If your income is variable or you have dependents, aim for the higher end." },
    { q: "Where should I keep it?", a: "In a liquid, low-risk account like a high-interest savings account or a TFSA holding cash — accessible quickly without penalty." },
  ],
};

export const savingsGoalConfig = {
  slug: "savings-goal-calculator-canada",
  name: "Savings Goal Calculator",
  category: "budgeting",
  icon: "Target",
  tagline: "Find the monthly amount needed to hit any savings goal.",
  applicableYear: 2026,
  lastUpdated: "2026-01-01",
  recommendedProduct: "personal-finance-dashboard",
  relatedTools: ["compound-interest-calculator-canada", "emergency-fund-calculator-canada"],
  seo: {
    title: "Savings Goal Calculator Canada (2026) — How Much to Save Each Month",
    description: "Find out how much to save each month to reach a goal — a car, wedding, down payment or vacation — including investment growth.",
    keywords: ["savings goal calculator", "how much to save per month", "savings target calculator canada", "down payment savings"],
  },
  inputs: [
    { id: "goalAmount", label: "Savings goal", type: "currency", default: 50000, min: 0, help: "e.g. a home down payment, car, or wedding." },
    { id: "currentSavings", label: "Already saved", type: "currency", default: 10000, min: 0 },
    { id: "years", label: "Years to reach goal", type: "slider", default: 5, min: 1, max: 40, step: 1, suffix: " yrs" },
    { id: "annualReturn", label: "Expected annual return", type: "percent", default: 4, min: 0, max: 20, step: 0.1, suffix: "%", help: "Use 0% for a plain savings account." },
  ],
  compute: (v) => {
    const r = savingsGoal(v);
    return {
      primary: { label: "Monthly amount needed", value: r.monthlyRequired, type: "currency2" },
      secondary: [
        { label: "Goal amount", value: r.goalAmount, type: "currency" },
        { label: "Total you'll contribute", value: r.totalContributions, type: "currency" },
        { label: "Growth earned", value: r.growthEarned, type: "currency" },
      ],
      chart: { type: "line", title: "Progress toward your goal", data: r.series.map((s) => ({ x: `Yr ${s.year}`, Balance: s.balance })), xKey: "x", series: [{ key: "Balance", name: "Balance" }] },
      downloadRows: () => [["Year", "Balance"], ...r.series.map((s) => [s.year, s.balance])],
    };
  },
  content: {
    howItWorks: "Tell the calculator your goal, what you've already saved, your timeline and expected return. It works backwards to find the monthly contribution needed to hit the target, accounting for investment growth along the way.",
    inputsExplained: [
      { term: "Savings goal", desc: "The total amount you're aiming for." },
      { term: "Expected return", desc: "Investment growth rate. Use 0% for a regular savings account, or a modest rate for invested funds." },
    ],
    example: "To reach $50,000 in 5 years starting from $10,000 at a 4% return, you'd need to save roughly $560/month.",
    assumptions: ["Constant monthly contributions.", "Constant return, compounded monthly.", "Contributions made monthly."],
    limitations: ["Investment returns aren't guaranteed.", "Ignores taxes on non-registered growth.", "Inflation may raise your real goal over time."],
  },
  faq: [
    { q: "Should I invest or use a savings account?", a: "For goals under ~2-3 years, a high-interest savings account or GIC is safer. For longer goals, investing may grow faster but carries risk — set the return accordingly." },
    { q: "What return should I assume?", a: "Use 0-2% for cash/HISA, 3-4% for conservative GIC/bond mixes, and higher only for long-term invested goals where you can tolerate risk." },
  ],
};
